import { useEffect, useState } from "react";
import Reveal from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import { EVENTS } from "@/content";

const MONTH_INDEX = {
  JAN: 0, FEB: 1, MAR: 2, APR: 3, MAY: 4, JUN: 5,
  JUL: 6, AUG: 7, SEP: 8, OCT: 9, NOV: 10, DEC: 11,
};

// Parse "AUG 09", "SEP 20–28", or "DEC 30–JAN 02" + year into start/end Dates.
function eventDates(event) {
  const parts = event.date.split(/\s*[–—-]\s*/);
  const [startMonthToken, startDayToken] = parts[0].trim().split(/\s+/);
  const startMonth = MONTH_INDEX[startMonthToken.toUpperCase()];
  let endMonth = startMonth;
  let endDay = parseInt(startDayToken, 10);
  let endYear = parseInt(event.year, 10) || new Date().getFullYear();
  const startYear = endYear;

  if (parts.length > 1) {
    const endTokens = parts[parts.length - 1].trim().split(/\s+/);
    if (endTokens.length > 1) {
      endMonth = MONTH_INDEX[endTokens[0].toUpperCase()];
      endDay = parseInt(endTokens[1], 10);
      if (endMonth < startMonth) endYear += 1; // range crossing into next year
    } else {
      endDay = parseInt(endTokens[0], 10);
    }
  }
  return {
    start: new Date(startYear, startMonth, parseInt(startDayToken, 10)),
    end: new Date(endYear, endMonth, endDay, 23, 59, 59, 999),
  };
}

// Dedupe: a calendar event duplicating a hand-listed one (same dates, shared
// significant words like "ministers retreat") is dropped — the curated static
// entry wins, so events like "TN Ministers Retreat" don't double up.
const DEDUPE_STOPWORDS = new Set([
  "worship", "church", "churches", "event", "events", "ministry", "ministries",
  "the", "with", "and", "for", "sunday", "morning", "night", "leading",
]);

function significantWords(name) {
  return (name || "")
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, " ")
    .split(/\s+/)
    .filter((w) => w.length >= 3 && !DEDUPE_STOPWORDS.has(w));
}

function isDuplicateOfStatic(calItem, staticItems) {
  const calWords = significantWords(calItem.event.name);
  return staticItems.some((s) => {
    const datesOverlap = calItem.start <= s.end && calItem.end >= s.start;
    if (!datesOverlap) return false;
    const staticWords = significantWords(s.event.name);
    return calWords.some((w) => staticWords.includes(w));
  });
}

export default function Events() {
  const [calendarEvents, setCalendarEvents] = useState([]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/calendar-events`)
      .then((r) => (r.ok ? r.json() : { events: [] }))
      .then((data) => {
        if (!cancelled) setCalendarEvents(data.events || []);
      })
      .catch(() => {}); // static list still renders if the sync is unavailable
    return () => { cancelled = true; };
  }, []);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const staticItems = EVENTS.map((event) => ({ event, ...eventDates(event) }));
  const calendarItems = calendarEvents
    .map((event) => ({
      event,
      start: new Date(`${event.start}T00:00:00`),
      end: new Date(`${event.end}T23:59:59.999`),
    }))
    .filter((i) => !isDuplicateOfStatic(i, staticItems));

  const upcomingEvents = [...staticItems, ...calendarItems]
    .filter((i) => i.end >= today)
    .sort((a, b) => a.start - b.start)
    .map((i) => i.event);

  return (
    <section id="dates" data-testid="events-section" className="py-24 md:py-36 px-6 md:px-12">
      <div className="max-w-7xl mx-auto">
        <SectionHeading number="03" title="Upcoming Events" testId="events-heading" />

        <div className="mt-14 md:mt-20">
          {upcomingEvents.length === 0 ? (
            <Reveal delay={0.1}>
              <div
                data-testid="events-empty-state"
                className="relative overflow-hidden border border-dashed border-ink/30"
              >
                <img
                  src="/assets/stage-blue-wide.webp"
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover opacity-[0.14]"
                />
                <div className="relative p-10 md:p-16 max-w-2xl">
                  <p className="font-spartan font-bold text-2xl md:text-3xl leading-snug">
                    New worship dates are being confirmed.
                  </p>
                  <p className="mt-4 text-sm md:text-base text-fog leading-relaxed">
                    Upcoming gatherings will be posted here soon. Planning something? The best
                    way to get a date on the calendar is to start an inquiry.
                  </p>
                  <a
                    href="#invite"
                    data-testid="events-invite-link"
                    className="mt-8 inline-flex items-center gap-3 text-xs font-semibold tracking-[0.22em] uppercase border-b border-ink pb-1 hover:gap-5 transition-[gap] duration-300"
                  >
                    Invite David <span aria-hidden="true">→</span>
                  </a>
                </div>
              </div>
            </Reveal>
          ) : (
            <ul data-testid="events-list" className="divide-y divide-ink/15 border-t border-b border-ink/15">
              {upcomingEvents.map((event, i) => (
                <Reveal as="li" key={`${event.name}-${i}`} delay={i * 0.06}>
                  <div className="py-8 md:py-10 grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 items-center group">
                    <div className="md:col-span-3">
                      <p className="font-spartan font-extrabold uppercase text-3xl md:text-4xl leading-none">
                        {event.date}
                      </p>
                      {event.year && <p className="mt-1 text-xs tracking-[0.25em] text-fog">{event.year}</p>}
                    </div>
                    <div className="md:col-span-6">
                      <p className="font-spartan font-bold text-xl md:text-2xl">{event.name}</p>
                      <p className="mt-1 text-sm text-fog">{event.location}</p>
                      {event.details && <p className="mt-2 text-sm text-fog/80">{event.details}</p>}
                    </div>
                    <div className="md:col-span-3 md:text-right">
                      {event.registerUrl && (
                        <a
                          href={event.registerUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`event-register-${i}`}
                          className="inline-block border-2 border-ink text-ink text-xs font-semibold tracking-[0.22em] uppercase px-7 py-3 rounded-full group-hover:bg-ink group-hover:text-paper transition-colors duration-300"
                        >
                          Register
                        </a>
                      )}
                    </div>
                  </div>
                </Reveal>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}
